import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import Modal from '../components/Modal';
import LegalDocumentView from '../components/LegalDocumentView';
import { AboutContent, LegalSectionContent } from '../types';

/**
 * Super Admin editor for the public About Us page. Only this role reaches
 * it (route-guarded and PUT-guarded server-side); everyone else sees the
 * read-only /about page.
 */
const AboutContentPage = () => {
  const [content, setContent] = useState<AboutContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(false);
  const [showSavedModal, setShowSavedModal] = useState(false);

  useEffect(() => {
    apiService
      .getAbout()
      .then(setContent)
      .catch((err: any) => setError(err.message || 'Failed to load the About Us page'))
      .finally(() => setLoading(false));
  }, []);

  const patchSection = (index: number, changes: Partial<LegalSectionContent>) =>
    setContent((prev) =>
      prev ? { ...prev, sections: prev.sections.map((s, i) => (i === index ? { ...s, ...changes } : s)) } : prev
    );

  const removeSection = (index: number) =>
    setContent((prev) => (prev ? { ...prev, sections: prev.sections.filter((_, i) => i !== index) } : prev));

  const moveSection = (index: number, delta: number) =>
    setContent((prev) => {
      if (!prev) return prev;
      const target = index + delta;
      if (target < 0 || target >= prev.sections.length) return prev;
      const sections = [...prev.sections];
      [sections[index], sections[target]] = [sections[target], sections[index]];
      return { ...prev, sections };
    });

  const addSection = () =>
    setContent((prev) => (prev ? { ...prev, sections: [...prev.sections, { heading: '', body: '' }] } : prev));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content) return;
    setError('');
    setSaving(true);
    try {
      // Re-render from what the server stored: it strips anything outside
      // the page's shape, so this is exactly what visitors will see.
      setContent(await apiService.updateAbout(content));
      setShowSavedModal(true);
    } catch (err: any) {
      setError(err.message || 'Failed to save the About Us page');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreOriginal = async () => {
    setError('');
    try {
      setContent(await apiService.getAboutDefault());
    } catch (err: any) {
      setError(err.message || 'Failed to load the original text');
    }
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1>About Us Page</h1>
        <p>
          The public About Us page — credits, municipality contacts, and anything else you want shown. Everyone
          can view it; only you can edit it. Changes go live as soon as you save.
        </p>
      </div>

      {error && (
        <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      {loading || !content ? (
        <p>Loading...</p>
      ) : (
        <form onSubmit={handleSave}>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <h3>Page</h3>
            </div>
            <div className="form-group">
              <label>Title</label>
              <input value={content.title} onChange={(e) => setContent({ ...content, title: e.target.value })} />
            </div>
          </div>

          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <h3>Sections</h3>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Plain text only. Leave a blank line between paragraphs, and start a line with <code>- </code> to
              make it a bullet. Formatting tags are not accepted and will be shown as written.
            </p>

            {content.sections.map((section, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid var(--border-light)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1rem',
                  marginBottom: '0.75rem'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem'
                  }}
                >
                  <strong style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Section {index + 1}</strong>
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button
                      className="btn btn-outline btn-sm"
                      type="button"
                      onClick={() => moveSection(index, -1)}
                      disabled={index === 0}
                      aria-label={`Move section ${index + 1} up`}
                    >
                      ↑
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      type="button"
                      onClick={() => moveSection(index, 1)}
                      disabled={index === content.sections.length - 1}
                      aria-label={`Move section ${index + 1} down`}
                    >
                      ↓
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      type="button"
                      onClick={() => removeSection(index)}
                      aria-label={`Remove section ${index + 1}`}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Heading</label>
                  <input value={section.heading} onChange={(e) => patchSection(index, { heading: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Text</label>
                  <textarea
                    value={section.body}
                    onChange={(e) => patchSection(index, { body: e.target.value })}
                    rows={6}
                  />
                </div>
              </div>
            ))}

            <button className="btn btn-outline btn-sm" type="button" onClick={addSection}>
              Add a section
            </button>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="btn btn-outline" type="button" onClick={() => setPreview(true)}>
              Preview
            </button>
            <button className="btn btn-outline" type="button" onClick={handleRestoreOriginal}>
              Restore the original text
            </button>
            <Link className="btn btn-outline" to="/about" target="_blank" rel="noopener noreferrer">
              View the live page
            </Link>
          </div>
        </form>
      )}

      {preview && content && (
        <Modal title="Preview — About Us" onClose={() => setPreview(false)}>
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <LegalDocumentView content={{ ...content, lastUpdated: '', draft: false }} />
          </div>
        </Modal>
      )}

      {showSavedModal && (
        <Modal title="Saved" onClose={() => setShowSavedModal(false)}>
          <p>The About Us page has been updated and is now live.</p>
        </Modal>
      )}
    </div>
  );
};

export default AboutContentPage;
